/**
 * The language the app is in.
 *
 * `t()` reads `state.settings.language`, which is reactive, so changing the
 * language re-renders every component that says anything — there is no reload
 * and nothing to refresh by hand.
 *
 * Adding a language is one file in `src/locales` and one line in `LANGUAGES`
 * (lib/languages.js, which the sign-in screen reads too).
 * `intl` is what `Intl` is handed for dates and money, which is not always the
 * same string as the catalogue's name: Serbian is written in two scripts and
 * the app uses the Latin one.
 */
import { computed, watch } from 'vue'
import { errorMessage, translate } from '../lib/i18n'
import { setDateLanguage } from '../lib/dates'
import en from '../locales/en'
import sr from '../locales/sr'
import { DEFAULT_LANGUAGE, LANGUAGES, rememberLanguage } from '../lib/languages'
import { setSetting } from './settings'
import { state } from './state'
import { allowed } from '../lib/usage'
import { count } from './usage'

export { DEFAULT_LANGUAGE, LANGUAGES }

const CATALOGUES = { en, sr }

/** The chosen language, or English when it names one that is not here. */
export const language = computed(() =>
  CATALOGUES[state.settings.language] ? state.settings.language : DEFAULT_LANGUAGE,
)

export const intlLocale = computed(
  () => LANGUAGES.find((l) => l.code === language.value)?.intl ?? 'en-GB',
)

/** Warn once per key, in development only: a hole is a bug, not a crash. */
const warned = new Set()
const onMissing = import.meta.env?.DEV
  ? (key) => {
      if (warned.has(key)) return
      warned.add(key)
      console.warn(`[i18n] ${language.value}: no translation for "${key}"`)
    }
  : undefined

/**
 * @param {string} key
 * @param {object} [params]  `{name}` placeholders, and `count` for plurals
 * @returns {string}
 */
export const t = (key, params) =>
  translate(key, params, {
    catalogue: CATALOGUES[language.value],
    fallback: en,
    locale: language.value,
    onMissing,
  })

/** A list for a picker, each language named in its own language. */
export const languageOptions = LANGUAGES.map((l) => ({ id: l.code, name: l.label }))

/**
 * Tells the rest of the app which language it is in: the date helpers, which
 * format month and day names, and the document itself, which a screen reader
 * and the browser's own spell-checker read off `<html lang>`.
 */
export function applyLanguage() {
  setDateLanguage({ locale: intlLocale.value, t })
  if (typeof document !== 'undefined') document.documentElement.lang = language.value
}

/** Kept in step with the setting, including the one that arrives with hydrate. */
watch(language, applyLanguage, { immediate: true })

/**
 * Picking a language is a setting like any other; the watcher does the rest.
 * The browser remembers it too, so the sign-in screen speaks it next time.
 */
export function setLanguage(code) {
  const chosen = LANGUAGES.some((l) => l.code === code) ? code : DEFAULT_LANGUAGE
  rememberLanguage(chosen)
  return setSetting('language', chosen)
}

const errorsCounted = new Map() // code → when, so one drawn again and again counts once a minute

/** Counts an error shown to the person — its code only, when it is one the list allows. */
function countError(err) {
  const code = err?.code
  if (typeof code !== 'string' || !allowed('error', code)) return
  const now = Date.now()
  if (now - (errorsCounted.get(code) ?? -Infinity) < 60_000) return
  errorsCounted.set(code, now)
  count('error', code)
}

/** An error, said in the reader's language (`errorMessage` in lib/i18n). */
export function errorText(err) {
  countError(err)
  return errorMessage(err, { catalogue: CATALOGUES[language.value], fallback: en, locale: language.value })
}
